import { useContext, useEffect, useState } from 'react';
import type { SectionKit } from './kit';
import { SsrContext } from '../ssr-context';
import { deadlineTime } from '../../model/pages';

/** "02d 11h 05m" style compact countdown for the announcement strip. Pure. */
export function compactLeft(ms: number): string {
  if (ms <= 0) return '';
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor(ms / 3_600_000) % 24;
  const m = Math.floor(ms / 60_000) % 60;
  const sec = Math.floor(ms / 1_000) % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return d > 0 ? `${d}d ${p(h)}h ${p(m)}m` : `${p(h)}h ${p(m)}m ${p(sec)}s`;
}

/** Announcement strip: one short line, an optional link, and an optional live
 *  countdown to `deadline`. PageView paints it as a slim full-width band (brand
 *  fill by default), so it reads as a bar above the page rather than a section. */
export default function BannerBlock({ k }: { k: SectionKit }) {
  const { section, txt, link, editable } = k;
  const target = deadlineTime(section.deadline);
  // Server-rendered pages hold a placeholder until mount: the HTML may be served
  // from the CDN long after it was rendered (see CountdownBlock).
  const ssr = useContext(SsrContext);
  const [now, setNow] = useState<number | null>(() => (ssr ? null : Date.now()));
  useEffect(() => {
    if (!Number.isFinite(target)) return;
    setNow((n) => n ?? Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  const left = !Number.isFinite(target) ? '' : now === null ? '--d --h --m' : compactLeft(target - now);
  // Editor canvas: an empty strip keeps its text slot, with a placeholder.
  const bodySlot = !!section.body || (editable && !section.label && !left);
  if (!bodySlot && !section.label && !left) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-[13px] font-semibold leading-snug sm:text-sm">
      {bodySlot && <span>{txt(section.body ?? '', (t) => ({ body: t }), false, 'Add an announcement')}</span>}
      {left && <span className="rounded-md bg-black/15 px-2 py-0.5 font-mono text-[12px] tabular-nums tracking-tight">{left}</span>}
      {section.label && section.href
        && link(section.label, section.href, 'inline-flex items-center gap-1 underline decoration-current/40 underline-offset-[3px] hover:decoration-current', undefined,
          <>{section.label} <span aria-hidden>→</span></>)}
    </div>
  );
}
